import React, { useEffect, useCallback } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

/**
 * Lightbox — fullscreen image viewer with keyboard + click navigation.
 * Props:
 *  - images: [{ src, alt, caption }]
 *  - index: current index (number) or null to close
 *  - onClose, onPrev, onNext
 */
export const Lightbox = ({ images, index, onClose, onPrev, onNext }) => {
  const open = index !== null && index !== undefined;

  const handleKey = useCallback(
    (e) => {
      if (!open) return;
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") onPrev();
      if (e.key === "ArrowRight") onNext();
    },
    [open, onClose, onPrev, onNext]
  );

  useEffect(() => {
    if (!open) return;
    document.addEventListener("keydown", handleKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handleKey);
      document.body.style.overflow = prev;
    };
  }, [open, handleKey]);

  if (!open) return null;
  const img = images[index];
  if (!img) return null;

  return (
    <div
      data-testid="lightbox"
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 backdrop-blur-md p-4 md:p-10"
      onClick={onClose}
    >
      <button
        type="button"
        aria-label="Close"
        data-testid="lightbox-close"
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
        className="absolute top-5 right-5 w-11 h-11 rounded-full glass-strong flex items-center justify-center text-white/85 hover:text-brand-orange hover:border-brand-orange/60 transition-all"
      >
        <X className="w-5 h-5" />
      </button>

      {images.length > 1 && (
        <>
          <button
            type="button"
            aria-label="Previous"
            data-testid="lightbox-prev"
            onClick={(e) => {
              e.stopPropagation();
              onPrev();
            }}
            className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full glass-strong flex items-center justify-center text-white/85 hover:text-brand-orange hover:border-brand-orange/60 transition-all"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          <button
            type="button"
            aria-label="Next"
            data-testid="lightbox-next"
            onClick={(e) => {
              e.stopPropagation();
              onNext();
            }}
            className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full glass-strong flex items-center justify-center text-white/85 hover:text-brand-orange hover:border-brand-orange/60 transition-all"
          >
            <ChevronRight className="w-6 h-6" />
          </button>
        </>
      )}

      <figure
        className="relative max-w-6xl max-h-full w-full flex flex-col items-center"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="relative w-full rounded-2xl overflow-hidden border border-brand-orange/25 shadow-[0_30px_120px_rgba(255,106,0,0.18)]">
          <img
            src={img.src}
            alt={img.alt || ""}
            className="w-full h-auto max-h-[78vh] object-contain bg-[#0B0C12]"
            loading="eager"
          />
        </div>
        {img.caption && (
          <figcaption className="mt-4 text-center text-sm md:text-base text-white/80 max-w-3xl">
            {img.caption}
          </figcaption>
        )}
        {images.length > 1 && (
          <div className="mt-2 label-mono text-white/55">
            {index + 1} / {images.length}
          </div>
        )}
      </figure>
    </div>
  );
};

export default Lightbox;
